/**
 * Project docs — spec §5-F6 ("nestable one level, reorderable, sidebar tree"),
 * §8.1 `/projects/:key/docs`.
 *
 * The pinned "📌 Project Guide" (V3 R4) is always first, and is highlighted
 * with its outstanding sections until it has actually been written.
 */

import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { FileText, FolderTree, Plus } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';
import { useProject } from '@/features/projects';
import { useCreateNote, useNotes, type NoteView } from './api';

export const PROJECT_GUIDE_TITLE = '📌 Project Guide';

function DocRow({ note, base, depth = 0 }: { note: NoteView; base: string; depth?: number }) {
  const isGuide = note.title === PROJECT_GUIDE_TITLE;
  return (
    <Link
      to={`${base}/${note._id}`}
      style={{ paddingLeft: 12 + depth * 20 }}
      className="flex items-center gap-2 border-b border-[var(--border)] py-2.5 pr-3 transition-colors duration-[120ms] hover:bg-[var(--surface-2)]"
    >
      <FileText className="size-4 shrink-0 text-[var(--text-muted)]" />
      <span className="truncate text-[13px] font-medium">{note.title}</span>
      {isGuide && <Badge tone="accent">Guide</Badge>}
      <span className="ml-auto shrink-0 text-[11px] vt-muted">
        {new Date(note.updatedAt).toLocaleDateString()}
      </span>
    </Link>
  );
}

export function ProjectDocsPage() {
  const { key } = useParams();
  const { data: project } = useProject(key);
  const navigate = useNavigate();
  const { data: notes, isLoading } = useNotes(
    project ? { scope: 'project', projectId: project._id } : {},
  );
  const create = useCreateNote();

  const base = `/projects/${key}/docs`;

  // One level of nesting: parents in order, each followed by its children.
  const tree = useMemo(() => {
    const all = notes ?? [];
    const guide = all.filter((n) => n.title === PROJECT_GUIDE_TITLE);
    const roots = all.filter((n) => !n.parentNoteId && n.title !== PROJECT_GUIDE_TITLE);
    const childrenOf = (id: string): NoteView[] => all.filter((n) => n.parentNoteId === id);
    return [...guide, ...roots].flatMap((root) => [
      { note: root, depth: 0 },
      ...childrenOf(root._id).map((child) => ({ note: child, depth: 1 })),
    ]);
  }, [notes]);

  return (
    <>
      <PageHeader
        title="Docs"
        subtitle={project ? `${project.name} — wiki, decisions and the project guide` : undefined}
        actions={
          <Button
            variant="primary"
            size="sm"
            data-testid="new-doc"
            disabled={!project}
            onClick={async () => {
              const note = await create.mutateAsync({
                scope: 'project',
                projectId: project!._id,
                title: 'Untitled doc',
              });
              navigate(`${base}/${note._id}`);
            }}
          >
            <Plus />
            New doc
          </Button>
        }
      />

      <div className="p-4">
        {isLoading || !project ? (
          <Skeleton className="h-32" />
        ) : tree.length === 0 ? (
          <EmptyState
            icon={<FolderTree className="size-6" />}
            title="No docs yet"
            description="Architecture notes, release checklists, ASO copy — the things a new joiner needs on day one."
            action={
              <Button
                variant="primary"
                onClick={async () => {
                  const note = await create.mutateAsync({
                    scope: 'project',
                    projectId: project._id,
                    title: 'Untitled doc',
                  });
                  navigate(`${base}/${note._id}`);
                }}
              >
                <Plus />
                Write the first doc
              </Button>
            }
          />
        ) : (
          <div className="vt-surface overflow-hidden" data-testid="docs-tree">
            {tree.map(({ note, depth }) => (
              <DocRow key={note._id} note={note} base={base} depth={depth} />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
